import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LeaveTypeSelector from './components/LeaveTypeSelector';
import LeaveMetricsCard from './components/LeaveMetricsCard';
import LeaveTrendChart from './components/LeaveTrendChart';
import LeaveBalanceAlerts from './components/LeaveBalanceAlerts';
import PolicyViolations from './components/PolicyViolations';
import EmployeeLeaveTable from './components/EmployeeLeaveTable';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import approvalWorkflowService from '../../services/approvalWorkflowService';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import EmptyStatePrompt from '../../components/EmptyStatePrompt';
import { employeeService } from '../../services/employeeService';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

const LeaveManagement = () => {
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState(['annual', 'sick', 'maternity', 'longService']);
  const [filters, setFilters] = useState({});
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [approvalInstances, setApprovalInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeEntitlements, setEmployeeEntitlements] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [balanceAlerts, setBalanceAlerts] = useState([]);
  const [violations, setViolations] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const employeesData = await employeeService?.getAll();
      setEmployees(employeesData || []);

      if (employeesData?.length > 0) {
        const [balanceAlertsData, violationsData, approvals] = await Promise.all([
          Promise.resolve([]),
          Promise.resolve([]),
          approvalWorkflowService?.getAllApprovalInstances()
        ]);
        setBalanceAlerts(balanceAlertsData);
        setViolations(violationsData);
        setApprovalInstances(approvals?.filter((a) => a?.entityType === 'leave_request'));
      }
    } catch (err) {
      setError(err?.message || 'Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleAddEmployees = () => {
    window.location.href = '/workforce-management-dashboard';
  };

  const handleLeaveTypeToggle = (typeId) => {
    setSelectedLeaveTypes((prev) =>
      prev?.includes(typeId) ?
        prev?.filter((id) => id !== typeId) :
        [...prev, typeId]
    );
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApprove = (employeeId) => {
    console.log('Approving leave for employee:', employeeId);
  };

  const handleReject = (employeeId) => {
    console.log('Rejecting leave for employee:', employeeId);
  };

  const handleViewDetails = (employeeId) => {
    console.log('Viewing details for employee:', employeeId);
  };

  const handleExport = (format) => {
    console.log('Exporting data in format:', format);
    setShowExportMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading leave data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no employees
  if (!employees || employees?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Leave Management Dashboard
            </h1>
            <p className="caption text-sm md:text-base text-muted-foreground">
              Comprehensive leave tracking and PNG Employment Act compliance monitoring
            </p>
          </div>

          <div className="bg-card rounded-lg border border-border p-8">
            <EmptyStatePrompt
              icon="Calendar"
              title="No Leave Records Available"
              description="Add employees to start tracking annual leave, sick leave, maternity leave, and long service leave entitlements. The system will automatically calculate accruals based on PNG Employment Act requirements."
              actionLabel="Add Employees"
              onAction={handleAddEmployees}
            />
          </div>
        </main>
      </div>
    );
  }

  const handleApproveRequest = async (requestId) => {
    try {
      // Check if approval workflow exists
      const existingApproval = approvalInstances?.find((a) => a?.entityId === requestId);

      if (existingApproval) {
        // Use approval workflow
        const pendingSteps = await approvalWorkflowService?.getPendingApprovals();
        const currentStep = pendingSteps?.find((s) => s?.entityId === requestId);

        if (currentStep) {
          await approvalWorkflowService?.approveStep(
            currentStep?.id,
            'admin@example.com',
            'Approved from leave management'
          );
        }
      } else {
        console.log('Approving request:', requestId);
      }

      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      // Check if approval workflow exists
      const existingApproval = approvalInstances?.find((a) => a?.entityId === requestId);

      if (existingApproval) {
        // Use approval workflow
        const pendingSteps = await approvalWorkflowService?.getPendingApprovals();
        const currentStep = pendingSteps?.find((s) => s?.entityId === requestId);

        if (currentStep) {
          const reason = prompt('Enter rejection reason:');
          if (reason) {
            await approvalWorkflowService?.rejectStep(
              currentStep?.id,
              'admin@example.com',
              reason
            );
          }
        }
      } else {
        console.log('Rejecting request:', requestId);
      }

      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to reject request');
    }
  };

  const handleInitiateApprovalWorkflow = async (requestId, requestData) => {
    try {
      const metadata = {
        department: requestData?.employee?.department,
        leave_type: requestData?.leaveType,
        days: requestData?.daysRequested
      };

      await approvalWorkflowService?.initiateApprovalWorkflow(
        'leave_request',
        requestId,
        metadata,
        'admin@example.com'
      );

      loadData();
    } catch (err) {
      setError(err?.message || 'Failed to initiate approval workflow');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <LegalDisclaimerBanner />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <DataRefreshIndicator />

      <main className="pt-32 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                Leave Management Dashboard
              </h1>
              <p className="caption text-sm md:text-base text-muted-foreground">
                Comprehensive leave tracking and PNG Employment Act compliance monitoring
              </p>
            </div>
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowExportMenu(!showExportMenu)}
                iconName="Download"
                iconPosition="left">

                Export Data
              </Button>
              {showExportMenu &&
              <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-md shadow-lg z-50">
                  <button
                  onClick={() => handleExport('payroll')}
                  className="w-full px-4 py-2 text-left caption text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2">

                    <Icon name="FileSpreadsheet" size={16} color="currentColor" />
                    Payroll Integration
                  </button>
                  <button
                  onClick={() => handleExport('audit')}
                  className="w-full px-4 py-2 text-left caption text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2">

                    <Icon name="FileText" size={16} color="currentColor" />
                    Compliance Audit
                  </button>
                  <button
                  onClick={() => handleExport('csv')}
                  className="w-full px-4 py-2 text-left caption text-sm text-foreground hover:bg-muted transition-smooth flex items-center gap-2">

                    <Icon name="Download" size={16} color="currentColor" />
                    CSV Export
                  </button>
                </div>
              }
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <LeaveTypeSelector
              selectedTypes={selectedLeaveTypes}
              onTypeToggle={handleLeaveTypeToggle} />

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <LeaveMetricsCard
              title="Total Leave Days Taken"
              value="2,145"
              subtitle="Last 12 months"
              icon="Calendar"
              trend="up"
              trendValue="12%"
              colorScheme="primary" />

            <LeaveMetricsCard
              title="Average Utilization Rate"
              value="78.5%"
              subtitle="Across all departments"
              icon="TrendingUp"
              trend="up"
              trendValue="5%"
              colorScheme="success" />

            <LeaveMetricsCard
              title="Pending Approvals"
              value="23"
              subtitle="Requires action"
              icon="Clock"
              trend="neutral"
              trendValue="0%"
              colorScheme="warning" />

            <LeaveMetricsCard
              title="Policy Violations"
              value="2"
              subtitle="Active cases"
              icon="AlertTriangle"
              trend="neutral"
              trendValue="0%"
              colorScheme="error" />

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <LeaveTrendChart
                data={[]}
                selectedTypes={selectedLeaveTypes} />

            </div>
            <div className="space-y-6">
              <LeaveBalanceAlerts alerts={[]} />
              <PolicyViolations violations={[]} />
            </div>
          </div>

          <EmployeeLeaveTable
            employees={[]}
            onApprove={handleApprove}
            onReject={handleReject}
            onViewDetails={handleViewDetails} />

        </div>
      </main>
      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>);

};

export default LeaveManagement;